/**
 * useComments Hook
 * Manages comment state and actions (accept, reject, edit).
 * All actions are persisted to the backend via API calls.
 */
import { useState, useCallback } from 'react';
import {
  generateFeedback,
  generateDocumentFeedback,
  getComments,
  acceptComment as apiAcceptComment,
  editComment as apiEditComment,
  rejectComment as apiRejectComment,
  dismissCategory as apiDismissCategory,
  createManualComment as apiCreateManualComment,
} from '../services/api';
import type {
  Comment,
  FeedbackRequest,
  DocumentFeedbackRequest,
  CommentSuggestion,
  Position,
} from '../types';

interface UseCommentsReturn {
  comments: Comment[];
  loading: boolean;
  error: string | null;
  selectedCommentId: string | null;
  fetchComments: (submissionId: string) => Promise<void>;
  generateForPage: (request: FeedbackRequest) => Promise<void>;
  generateForDocument: (request: DocumentFeedbackRequest) => Promise<void>;
  acceptComment: (id: string) => Promise<void>;
  rejectComment: (id: string, reason: string) => Promise<void>;
  editComment: (id: string, newText: string) => Promise<void>;
  dismissCategory: (submissionId: string, category: string) => Promise<number>;
  createManualComment: (
    submissionId: string,
    pageNumber: number,
    highlightedText: string,
    suggestion: string,
    category: string,
    position: Position,
    positions?: Position[]
  ) => Promise<Comment>;
  selectComment: (id: string | null) => void;
  getCommentsForPage: (pageNumber: number) => Comment[];
  clearComments: () => void;
}

export function useComments(): UseCommentsReturn {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);

  const fetchComments = useCallback(async (submissionId: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await getComments(submissionId);
      // Transform API response to Comment type
      const transformedComments: Comment[] = (response.comments || []).map(
        (c: any, index: number) => ({
          id: c.id || `comment-${index}`,
          submissionId: submissionId,
          pageNumber: c.page_number || 1,
          position: c.position || { x: 0.1, y: 0.1, width: 0.3, height: 0.05 },
          positions: c.positions,
          highlightedText: c.highlighted_text || '',
          suggestion: c.suggestion || '',
          confidence: c.confidence || 'medium',
          category: c.category || 'general',
          status: c.status || 'pending',
          source: c.source || 'ai',
          finalText: c.final_text,
          rejectionReason: c.rejection_reason,
        })
      );
      setComments(transformedComments);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch comments');
    } finally {
      setLoading(false);
    }
  }, []);

  const generateForPage = useCallback(async (request: FeedbackRequest) => {
    setLoading(true);
    setError(null);

    try {
      const response = await generateFeedback(request);

      // Transform and add new comments, using server-generated IDs
      const newComments: Comment[] = response.comments.map(
        (c: CommentSuggestion, index: number) => ({
          id: c.id || `${request.submission_id}-${request.page_number}-${index}-${Date.now()}`,
          submissionId: request.submission_id,
          pageNumber: request.page_number,
          position: c.position,
          positions: c.positions,
          highlightedText: c.highlighted_text,
          suggestion: c.suggestion,
          confidence: c.confidence,
          category: c.category,
          status: 'pending' as const,
          source: 'ai' as const,
        })
      );

      setComments((prev) => [...prev, ...newComments]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate feedback');
    } finally {
      setLoading(false);
    }
  }, []);

  const generateForDocument = useCallback(async (request: DocumentFeedbackRequest) => {
    setLoading(true);
    setError(null);

    try {
      const response = await generateDocumentFeedback(request);

      // Transform and add new comments, using server-generated IDs
      const newComments: Comment[] = response.comments.map(
        (c: CommentSuggestion, index: number) => ({
          id: c.id || `${request.submission_id}-doc-${index}-${Date.now()}`,
          submissionId: request.submission_id,
          pageNumber: c.page_number || 1,
          position: c.position,
          positions: c.positions, // Multi-line positions
          highlightedText: c.highlighted_text,
          suggestion: c.suggestion,
          confidence: c.confidence,
          category: c.category,
          status: 'pending' as const,
          source: 'ai' as const,
        })
      );

      // Replace all comments with new ones (document-level feedback replaces page-level)
      setComments(newComments);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate feedback');
    } finally {
      setLoading(false);
    }
  }, []);

  const clearComments = useCallback(() => {
    setComments([]);
    setSelectedCommentId(null);
  }, []);

  const acceptComment = useCallback(async (id: string) => {
    // Optimistic update
    setComments((prev) =>
      prev.map((c) => (c.id === id ? { ...c, status: 'accepted' as const } : c))
    );

    try {
      await apiAcceptComment(id);
    } catch (err) {
      // Rollback on error
      setComments((prev) =>
        prev.map((c) => (c.id === id ? { ...c, status: 'pending' as const } : c))
      );
      setError(err instanceof Error ? err.message : 'Failed to accept comment');
      throw err;
    }
  }, []);

  const rejectComment = useCallback(async (id: string, reason: string) => {
    // Optimistic update
    setComments((prev) =>
      prev.map((c) =>
        c.id === id
          ? { ...c, status: 'rejected' as const, rejectionReason: reason }
          : c
      )
    );

    try {
      await apiRejectComment(id, reason);
    } catch (err) {
      // Rollback on error
      setComments((prev) =>
        prev.map((c) =>
          c.id === id
            ? { ...c, status: 'pending' as const, rejectionReason: undefined }
            : c
        )
      );
      setError(err instanceof Error ? err.message : 'Failed to reject comment');
      throw err;
    }
  }, []);

  const editComment = useCallback(async (id: string, newText: string) => {
    const comment = comments.find((c) => c.id === id);
    if (!comment) return;

    // Optimistic update
    setComments((prev) =>
      prev.map((c) =>
        c.id === id ? { ...c, status: 'edited' as const, finalText: newText } : c
      )
    );

    try {
      await apiEditComment(id, newText);
    } catch (err) {
      // Rollback on error
      setComments((prev) =>
        prev.map((c) =>
          c.id === id
            ? { ...c, status: 'pending' as const, finalText: undefined }
            : c
        )
      );
      setError(err instanceof Error ? err.message : 'Failed to edit comment');
      throw err;
    }
  }, [comments]);

  const dismissCategory = useCallback(
    async (submissionId: string, category: string): Promise<number> => {
      // Get comments that will be affected
      const affectedIds = comments
        .filter(
          (c) =>
            c.submissionId === submissionId &&
            c.category === category &&
            c.status === 'pending'
        )
        .map((c) => c.id);

      // Optimistic update
      setComments((prev) =>
        prev.map((c) =>
          affectedIds.includes(c.id)
            ? {
                ...c,
                status: 'rejected' as const,
                rejectionReason: `Category dismissed: ${category}`,
              }
            : c
        )
      );

      try {
        const response = await apiDismissCategory(submissionId, category);
        return response.count;
      } catch (err) {
        // Rollback on error
        setComments((prev) =>
          prev.map((c) =>
            affectedIds.includes(c.id)
              ? { ...c, status: 'pending' as const, rejectionReason: undefined }
              : c
          )
        );
        setError(err instanceof Error ? err.message : 'Failed to dismiss category');
        throw err;
      }
    },
    [comments]
  );

  const createManualComment = useCallback(
    async (
      submissionId: string,
      pageNumber: number,
      highlightedText: string,
      suggestion: string,
      category: string,
      position: Position,
      positions?: Position[]
    ): Promise<Comment> => {
      try {
        const response = await apiCreateManualComment({
          submission_id: submissionId,
          page_number: pageNumber,
          highlighted_text: highlightedText,
          suggestion,
          category,
          position,
          positions,
        });

        // Transform API response to Comment type
        const newComment: Comment = {
          id: response.id,
          submissionId: response.submissionId || submissionId,
          pageNumber: response.pageNumber || pageNumber,
          position: response.position || position,
          positions: response.positions,
          highlightedText: response.highlightedText || highlightedText,
          suggestion: response.suggestion || suggestion,
          confidence: response.confidence || 'high',
          category: response.category || category,
          status: response.status || 'accepted',
          source: 'manual',
          finalText: response.finalText,
          rejectionReason: response.rejectionReason,
        };

        setComments((prev) => [...prev, newComment]);
        return newComment;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create comment');
        throw err;
      }
    },
    []
  );

  const selectComment = useCallback((id: string | null) => {
    setSelectedCommentId(id);
  }, []);

  const getCommentsForPage = useCallback(
    (pageNumber: number): Comment[] => {
      return comments.filter((c) => c.pageNumber === pageNumber);
    },
    [comments]
  );

  return {
    comments,
    loading,
    error,
    selectedCommentId,
    fetchComments,
    generateForPage,
    generateForDocument,
    acceptComment,
    rejectComment,
    editComment,
    dismissCategory,
    createManualComment,
    selectComment,
    getCommentsForPage,
    clearComments,
  };
}

export default useComments;
